import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { Smartphone, QrCode, RefreshCw, CheckCircle2, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { API } from "../config/api";
import { useAuth } from "../context/AuthContext";

// Desktop view: generates a one-time handoff, encodes it into a QR
// that the user scans from their phone (already-authenticated session).
// Polls the backend every 2s; on `claimed` we drop the JWT into context
// and bounce to the dashboard.
const POLL_INTERVAL_MS = 2000;
const QRSignInPage = () => {
  const navigate = useNavigate();
  const { setSessionFromQR, user } = useAuth();
  const [handoffId, setHandoffId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [status, setStatus] = useState("loading"); // loading | ready | claimed | expired | error
  const pollRef = useRef(null);

  const claimUrl = handoffId ? `${window.location.origin}/qr-claim/${handoffId}` : "";

  const startHandoff = async () => {
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/auth/qr/start`, {});
      const { handoff_id, expires_in_seconds } = res.data;
      setHandoffId(handoff_id);
      const exp = Date.now() + expires_in_seconds * 1000;
      setExpiresAt(exp);
      setSecondsLeft(expires_in_seconds);
      setStatus("ready");
    } catch (err) {
      setStatus("error");
      toast.error(err.response?.data?.detail || "Couldn't start QR sign-in.");
    }
  };

  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
      return;
    }
    startHandoff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown
  useEffect(() => {
    if (!expiresAt) return undefined;
    const tick = setInterval(() => {
      const s = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
      setSecondsLeft(s);
      if (s === 0) setStatus((cur) => (cur === "ready" ? "expired" : cur));
    }, 500);
    return () => clearInterval(tick);
  }, [expiresAt]);

  // Poller
  useEffect(() => {
    if (status !== "ready" || !handoffId) return undefined;
    let alive = true;
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/auth/qr/poll/${handoffId}`);
        if (!alive) return;
        if (res.data.status === "claimed") {
          clearInterval(pollRef.current);
          setStatus("claimed");
          setSessionFromQR(res.data);
          toast.success("Signed in via QR — welcome!");
          setTimeout(() => navigate("/dashboard", { replace: true }), 700);
        } else if (res.data.status === "expired") {
          clearInterval(pollRef.current);
          setStatus("expired");
        }
      } catch {
        // Network hiccup — keep polling.
      }
    }, POLL_INTERVAL_MS);
    return () => {
      alive = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [status, handoffId, navigate, setSessionFromQR]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black flex items-center justify-center px-4 py-12" data-testid="qr-signin-page">
      <div className="w-full max-w-md bg-gray-900/80 backdrop-blur border border-gray-800 rounded-2xl p-8">
        <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-widest mb-3">
          <QrCode className="h-4 w-4" /> Sign in with phone
        </div>
        <h1 className="text-white text-2xl font-semibold mb-2">Scan to sign in</h1>
        <p className="text-gray-400 text-sm mb-6">
          Open <span className="text-emerald-300 font-medium">GlobalEyes.TV</span> on the phone you're already signed in on, then scan this code.
        </p>

        <div className="flex flex-col items-center bg-white rounded-xl p-4 mb-4 relative" data-testid="qr-code-wrap">
          {status === "ready" && handoffId && (
            <QRCodeCanvas
              value={claimUrl}
              size={232}
              level="M"
              includeMargin={false}
              data-testid="qr-code-canvas"
            />
          )}
          {status === "loading" && (
            <div className="h-[232px] w-[232px] flex items-center justify-center text-gray-400">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          )}
          {status === "claimed" && (
            <div className="h-[232px] w-[232px] flex items-center justify-center text-emerald-600">
              <CheckCircle2 className="h-14 w-14" />
            </div>
          )}
          {(status === "expired" || status === "error") && (
            <div className="h-[232px] w-[232px] flex flex-col items-center justify-center gap-3 text-gray-700">
              <RefreshCw className="h-10 w-10" />
              <p className="text-sm text-center">{status === "expired" ? "Code expired" : "Couldn't start"}</p>
            </div>
          )}
        </div>

        {status === "ready" && (
          <p className="text-gray-500 text-xs text-center mb-4" data-testid="qr-countdown">
            Code expires in <span className="text-emerald-400 font-medium">{Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, "0")}</span>
          </p>
        )}

        {(status === "expired" || status === "error") && (
          <Button
            onClick={startHandoff}
            className="w-full bg-emerald-600 hover:bg-emerald-700 mb-3"
            data-testid="qr-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Generate a new code
          </Button>
        )}

        <div className="bg-gray-800/60 border border-gray-700/60 rounded-lg p-3 flex items-start gap-2.5 mb-4">
          <Smartphone className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
          <div className="text-xs text-gray-300 leading-relaxed">
            <span className="font-semibold text-emerald-400">No phone signed in yet?</span>{" "}
            Use the email magic-link or password instead.
          </div>
        </div>

        <Link to="/" className="block">
          <Button variant="outline" className="w-full border-gray-700 text-gray-300 hover:bg-gray-800" data-testid="qr-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to home
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default QRSignInPage;
